import { encode } from '@toon-format/toon';
import type {
  AllocationOfCostsChartResponseByCategory,
  AllocationOfCostsChartResponseByCategoryDetails,
} from '../../api/financials-client/financials-client.type';
import { AllocationOfCostsCategory } from '../../api/financials-client/financials-client.type';
import {
  FIELD_SCHEMA,
  type FlatSummary,
  type FlatTeamData,
  type FlatTimeSeries,
  type FlattenedAllocationOfCostsData,
} from './allocation-of-costs.type';

/**
 * Flattens the options object into summary rows for total and average values.
 * @param {AllocationOfCostsChartResponseByCategoryDetails} categoryDetails - The details for a single category
 * @returns {FlatSummary[]} Array of flattened summary rows
 */
const flattenSummaries = (categoryDetails: AllocationOfCostsChartResponseByCategoryDetails): FlatSummary[] => {
  const summaries: FlatSummary[] = [];

  if (!categoryDetails.options) {
    return summaries;
  }

  for (const [optionName, optionData] of Object.entries(categoryDetails.options)) {
    // Add total row
    summaries.push({
      opt: optionName,
      type: 'total',
      t: optionData.total.tasks,
      tu: optionData.total.tasks_in_usd,
      p: optionData.total.points,
      pu: optionData.total.points_in_usd,
    });

    // Add average row
    summaries.push({
      opt: optionName,
      type: 'avg',
      t: optionData.average.tasks,
      tu: optionData.average.tasks_in_usd,
      p: optionData.average.points,
      pu: optionData.average.points_in_usd,
    });
  }

  return summaries;
};

/**
 * Flattens the chart_data object into time series rows (totals per period/option).
 * @param {AllocationOfCostsChartResponseByCategoryDetails} categoryDetails - The details for a single category
 * @returns {FlatTimeSeries[]} Array of flattened time series rows
 */
const flattenTimeSeries = (categoryDetails: AllocationOfCostsChartResponseByCategoryDetails): FlatTimeSeries[] => {
  const timeSeries: FlatTimeSeries[] = [];

  if (!categoryDetails.chart_data) {
    return timeSeries;
  }

  for (const [period, periodData] of Object.entries(categoryDetails.chart_data)) {
    for (const [optionName, optionData] of Object.entries(periodData)) {
      timeSeries.push({
        per: period,
        opt: optionName,
        t: optionData.total.tasks,
        tu: optionData.total.tasks_in_usd,
        p: optionData.total.points,
        pu: optionData.total.points_in_usd,
      });
    }
  }

  return timeSeries;
};

/**
 * Flattens the team data from chart_data into rows for each period/option/team combination.
 * @param {AllocationOfCostsChartResponseByCategoryDetails} categoryDetails - The details for a single category
 * @returns {FlatTeamData[]} Array of flattened team data rows
 */
const flattenTeamData = (categoryDetails: AllocationOfCostsChartResponseByCategoryDetails): FlatTeamData[] => {
  const teamData: FlatTeamData[] = [];

  if (!categoryDetails.chart_data) {
    return teamData;
  }

  for (const [period, periodData] of Object.entries(categoryDetails.chart_data)) {
    for (const [optionName, optionData] of Object.entries(periodData)) {
      if (!optionData.teams) {
        continue;
      }

      for (const [teamName, teamValues] of Object.entries(optionData.teams)) {
        teamData.push({
          per: period,
          opt: optionName,
          team: teamName,
          t: teamValues.tasks,
          tu: teamValues.tasks_in_usd,
          p: teamValues.points,
          pu: teamValues.points_in_usd,
        });
      }
    }
  }

  return teamData;
};

/**
 * Formats the allocation of costs data for Flora by transforming the nested structure
 * into a flattened format optimized for TOON encoding.
 *
 * The function:
 * 1. Extracts only the selected category's data (not all 11 categories)
 * 2. Flattens nested objects into uniform arrays (summaries, timeSeries, teamData)
 * 3. Uses abbreviated field names to reduce token count
 * 4. Includes a schema mapping for Flora to interpret the abbreviations
 *
 * @param {AllocationOfCostsChartResponseByCategory} data - The allocation of costs data by category
 * @param {AllocationOfCostsCategory} category - The selected category to format
 * @returns {string} TOON-encoded string of the flattened data, or empty string if no data
 */
const formatFloraAllocationOfCostsData = (
  data: AllocationOfCostsChartResponseByCategory | undefined,
  category: AllocationOfCostsCategory,
): string => {
  if (!data) {
    return '';
  }

  const categoryDetails = data[category];
  if (!categoryDetails) {
    return '';
  }

  const flattenedData: FlattenedAllocationOfCostsData = {
    schema: FIELD_SCHEMA,
    cat: category,
    summaries: flattenSummaries(categoryDetails),
    timeSeries: flattenTimeSeries(categoryDetails),
    teamData: flattenTeamData(categoryDetails),
  };

  // Don't encode if there's no meaningful data
  if (
    flattenedData.summaries.length === 0 &&
    flattenedData.timeSeries.length === 0 &&
    flattenedData.teamData.length === 0
  ) {
    return '';
  }

  return encode(flattenedData);
};

export { formatFloraAllocationOfCostsData };
